import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";
import React, { useEffect } from "react";
import koalaApiClient from "src/apis/koala-api-client";
import koalaUtils from "src/helpers/koala/koalaUtils";
import getCountryCodeFromLocale from "src/helpers/localization-helpers/getCountryCodeFromLocale";
import getTranslations from "src/helpers/localization-helpers/getTranslations";
import Layout from "src/sections/layout/Layout";
import KoalaAddToCartForm from "src/sections/product-overview/components/KoalaAddToCartForm";
import { KoalaProduct } from "src/types/koala/KoalaProduct";
import { Locale } from "src/types/localization/Locale";
import Container from "src/sections/container/Container";
import KoalaProductOverview from "src/sections/product-overview/KoalaProductOverview";
import { KoalaVariant } from "src/types/koala/KoalaVariant";
import { useRouter } from "next/router";
import useUrlVariant from "src/hooks/useUrlVariantId";
import addVariantParamsToQuery from "src/helpers/router-helpers/addVariantParamsToQuery";
import MediaMentions from "src/sections/media-feedback/MediaMentions";
import HowDoesShemsiWork from "src/sections/product-overview/components/HowDoesShemsiWork";
import ReviewsSection from "src/sections/product-overview/components/reviews/ReviewsSection";
import reviewsPerProduct, { Review } from "src/config/reviewsPerProduct";
import useCountryData from "src/hooks/useCountryData";
import reviewsPerProductUSA from "src/config/reviewsPerProductUSA";
import { cond, matchesProperty } from "lodash";
import useMainKoalaProducts from "src/hooks/useMainKoalaProducts";
import KoalaProductsListWithDesc from "src/sections/products-list/KoalaProductsListWithDesc";
import ExploreShemsiCTASection from "src/sections/homepage/ExploreShemsiCTASection";
import { matches } from "lodash/fp";
import EventTracker from "src/helpers/event-tracking/eventTrackingManager";
import getDescriptionData from "src/helpers/getDescriptionData";

interface Props {
  product: KoalaProduct;
  variant: KoalaVariant;
}

function KoalaProductPage({ product, variant: initialVariant }: Props) {
  const t = useTranslations("Product");
  const tOverView = useTranslations("ProductOverview");
  const router = useRouter();
  const countryData = useCountryData();
  const variant = useUrlVariant(product, initialVariant);
  const otherProducts = useMainKoalaProducts();
  const descriptionData = getDescriptionData(product.handle);

  useEffect(
    function setInitialUrlOptions() {
      const selectedOptions = koalaUtils.mapQueryToSelectedOptions(router.query);
      const urlVariant = product.variants.find(matches({ selectedOptions }));

      if (!urlVariant || urlVariant.quantity <= 0) {
        const firstVariantAvailable = koalaUtils.getAvailableVariant(product, selectedOptions);
        if (firstVariantAvailable) {
          addVariantParamsToQuery(router, firstVariantAvailable.selectedOptions);
        }
      }
    },
    [router.query, product]
  );

  useEffect(() => {
    if (product && variant) {
      EventTracker.viewContent(product, variant);
    }
  }, [product, variant]);

  let productReviews: Review[] = [];
  if (countryData) {
    productReviews =
      (countryData.code == "US" ? reviewsPerProductUSA : reviewsPerProduct).find(
        matchesProperty("internalProductName", product.handle)
      )?.reviews || [];
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    productID: variant && variant?._id,
    image: product.images[0].url,
    description: tOverView(descriptionData?.description),
    brand: {
      "@type": "Brand",
      name: "Shemsi",
    },
    sku: variant && variant?.sku,
    offers: {
      "@type": "Offer",
      url: `https://myshemsi.com/product/${product.handle}`,
      priceCurrency: "EGP",
      price: variant && variant?.price,
      availability: variant && variant?.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      condition: "https://schema.org/NewCondition",
    },
  };

  const jsonLdString = JSON.stringify(jsonLd);

  return (
    <Layout
      pageTitle={product.name}
      description={t("product_description")}
      stickyFooterButtonTitle={t("get_product", {
        product_name: product.name,
      })}
      showStickyFooterPopUpButton={true}
      StickyFooterPopUpComponent={
        variant ? <KoalaAddToCartForm product={product} variant={variant} stickyFooter={true} /> : <></>
      }
    >
      <Head>
        <meta httpEquiv="pragma" content="no-cache" />
        <meta httpEquiv="cache-control" content="no-cache, no-store, must-revalidate" />

        <meta property="og:title" content={product?.name || "Shemsi Product"} />
        <meta property="og:description" content={tOverView(descriptionData?.description) || "High-quality product"} />
        <meta property="og:url" content={`https://myshemsi.com/product/${product?.handle || ""}`} />
        <meta property="og:image" content={product?.images?.[0]?.url || "/default-image.jpg"} />
        <meta property="og:type" content="product" />
        <meta property="product:brand" content={"Shemsi"} />
        <meta
          property="product:availability"
          content={variant && variant?.quantity > 0 ? "In Stock" : "Out Of Stock"}
        />
        <meta property="product:condition" content="new" />
        <meta property="product:price:amount" content={variant && variant.price.toString()} />
        <meta property="product:price:currency" content={variant && variant.currency} />
        <meta property="product:retailer_item_id" content={variant && variant._id} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdString }} />
      </Head>

      <Container className="pb-4 pt-3 sm:pb-4 sm:pt-3 lg:py-10">
        <section>
          <KoalaProductOverview product={product} variant={variant} />
        </section>
      </Container>

      <div className="bg-beige-50">
        <Container>
          <section>
            <MediaMentions />
          </section>
        </Container>
      </div>

      <Container>
        <HowDoesShemsiWork />
      </Container>

      {productReviews && (
        <Container>
          <ReviewsSection productReviews={productReviews} productHandle={product.handle} />
        </Container>
      )}

      <div className="bg-beige-400">
        <Container>
          <section className="flex flex-col items-center gap-8">
            <h2 className="text-2xl font-bold lg:text-3xl">{t("check_out_other_products")}</h2>
            <KoalaProductsListWithDesc products={otherProducts} />
          </section>
        </Container>
      </div>
    </Layout>
  );
}

// params, locale, query
export const getServerSideProps: GetServerSideProps = async ({ params, locale, query }) => {
  // 1. Get product
  const handle = params?.productHandle as string;
  const countryCode = getCountryCodeFromLocale(locale as Locale);
  const productResult = await koalaApiClient.getProducts({
    handle,
    countryCode,
  });
  if (productResult.isErr()) {
    return {
      notFound: true,
    };
  }
  const product = productResult.value.data[0];

  // 2. Get variant
  let variant = koalaUtils.getVariantFromQuery(product, query);
  if (!variant) variant = product.variants.find(koalaUtils.isVariantAvailable);

  // 3. translations
  const translations = getTranslations(locale as Locale);

  return { props: { product, variant, translations } };
};

export default KoalaProductPage;
